import { useState } from "react";
import { C } from "../../theme/tokens";

/* Shared pieces for every card where the user types their trading
   account ID (RegistrationScreen, BrowserAuthScreen, TrialRenewalScreen).

   - ProviderSwitch: sliding two-option toggle, Pocket Option / BabaOption.
   - useAccountIdField: owns the selected provider + the ID text and
     filters what can be typed for the selected provider:
       pocket -> digits only
       baba   -> latin letters + digits (max 32), a pasted "babaoption-"
                 prefix is stripped.
     Switching provider clears the field so an ID typed for one platform
     never lingers in the field of the other. */

export const PROVIDER_POCKET = "pocket";
export const PROVIDER_BABA = "baba";

const PROVIDER_OPTIONS = [
  { key: PROVIDER_POCKET, label: "Pocket Option" },
  { key: PROVIDER_BABA, label: "BabaOption" },
];

const BABA_MAX_LEN = 32;
const BABA_PREFIX_RE = /^\s*babaoption-/i;

export function sanitizeAccountId(provider, raw) {
  const value = String(raw ?? "");
  if (provider === PROVIDER_BABA) {
    return value
      .replace(BABA_PREFIX_RE, "")
      .replace(/[^A-Za-z0-9]/g, "")
      .slice(0, BABA_MAX_LEN);
  }
  return value.replace(/[^0-9]/g, "");
}

export function useAccountIdField() {
  const [provider, setProviderState] = useState(PROVIDER_POCKET);
  const [accountId, setAccountId] = useState("");

  const setProvider = (next) => {
    if (next === provider) return;
    setProviderState(next);
    setAccountId("");
  };

  const onChange = (e) => setAccountId(sanitizeAccountId(provider, e.target.value));

  const isBaba = provider === PROVIDER_BABA;

  // Spread onto the <input>; keeps value/filtering/keyboard type in one place.
  const inputProps = (t) => ({
    value: accountId,
    onChange,
    placeholder: isBaba ? t.reg_id_placeholder_baba : t.reg_id_placeholder,
    inputMode: isBaba ? "text" : "numeric",
    autoComplete: "off",
    autoCapitalize: "none",
    autoCorrect: "off",
    spellCheck: false,
    maxLength: isBaba ? BABA_MAX_LEN : 32,
  });

  const hint = (t) => (isBaba ? t.reg_id_hint_baba : t.reg_id_hint_pocket);

  return { provider, setProvider, accountId, inputProps, hint, isBaba };
}

export function ProviderSwitch({ provider, onChange, t, disabled = false, style }) {
  const activeIndex = Math.max(
    0,
    PROVIDER_OPTIONS.findIndex((o) => o.key === provider)
  );

  return (
    <div
      role="tablist"
      aria-label={t.reg_provider_label}
      style={{
        position: "relative",
        display: "flex",
        boxSizing: "border-box",
        background: C.panel1,
        border: `1px solid ${C.hairline}`,
        borderRadius: 14,
        padding: 4,
        margin: "10px 0",
        opacity: disabled ? 0.6 : 1,
        ...style,
      }}
    >
      {/* sliding thumb */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          top: 4,
          bottom: 4,
          left: 4,
          width: "calc(50% - 4px)",
          borderRadius: 10,
          background: `linear-gradient(120deg, ${C.greenBright} 0%, ${C.green} 100%)`,
          transform: `translateX(${activeIndex * 100}%)`,
          transition: "transform .22s ease",
          pointerEvents: "none",
        }}
      />

      {PROVIDER_OPTIONS.map((opt) => {
        const active = opt.key === provider;
        return (
          <button
            key={opt.key}
            type="button"
            role="tab"
            aria-selected={active}
            disabled={disabled}
            onClick={() => onChange(opt.key)}
            style={{
              position: "relative",
              zIndex: 1,
              flex: 1,
              padding: "11px 10px",
              borderRadius: 10,
              border: "none",
              background: "transparent",
              color: active ? "#0b0d13" : C.secondary,
              fontFamily: "'Space Grotesk', sans-serif",
              fontWeight: 700,
              fontSize: 13.5,
              cursor: disabled ? "not-allowed" : "pointer",
              transition: "color .22s ease",
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
